import { useMemo } from 'react';
import { ConfigurationData } from '../types';

export interface PreselectedValuesResult {
  options: string[];
  allowCustom: boolean;
  loading: boolean;
}

/**
 * Hook to extract predefined values for an element from existing configuration data
 * Uses the existing predefined_values field from ELEMENT_DEFINITIONS in the backend
 */
export const usePreselectedValues = (
  elementName: string,
  config?: ConfigurationData | null
): PreselectedValuesResult => {
  return useMemo(() => {
    // Return loading state if config is not available yet
    if (!config) {
      return {
        options: [],
        allowCustom: true,
        loading: true,
      };
    }

    // Check if element exists in configuration
    if (!elementName || !config.elements[elementName]) {
      return {
        options: [],
        allowCustom: true,
        loading: false,
      };
    }

    const element = config.elements[elementName];

    return {
      options: element.predefined_values ?? [],
      allowCustom: element.allow_custom_value !== false,
      loading: false,
    };
  }, [elementName, config]);
};

/**
 * Utility function to check if an element supports multi-select values
 * Based on the operator type and value_type from element definition
 */
export const useIsMultiSelectElement = (
  elementName: string,
  operatorSymbol: string,
  config?: ConfigurationData | null
): boolean => {
  return useMemo(() => {
    if (!config || !elementName || !config.elements[elementName]) {
      return false;
    }

    const element = config.elements[elementName];

    // Check if this is a list-type operator (in, exactlyMatches, etc.)
    const listOperators = ['in', 'exactlyMatches'];
    const isListOperator = listOperators.includes(operatorSymbol);

    // Check if element type supports arrays
    const isArrayType =
      element.value_type === 'string_array' ||
      element.type === 'string_array' ||
      element.allow_multiple_values === true;

    return isListOperator && (isArrayType || (element.predefined_values?.length ?? 0) > 0);
  }, [elementName, operatorSymbol, config]);
};

/**
 * Utility function to get example values for help text
 */
export const useElementExamples = (
  elementName: string,
  config?: ConfigurationData | null
): string[] => {
  return useMemo(() => {
    if (!config || !elementName || !config.elements[elementName]) {
      return [];
    }

    return config.elements[elementName].examples ?? [];
  }, [elementName, config]);
};
